package com.devsusana.portfolio.pages.posts.es

import androidx.compose.runtime.Composable
import com.devsusana.portfolio.components.blog.*
import com.varabyte.kobweb.core.Page

@Page("/posts/es/android-memory-engineering-llm")
@Composable
fun AndroidMemoryEngineeringLlmPageEs() {
    ArticleScaffold(
        title = "Ingeniería de Memoria RAM en Android 15-17: Ejecutando LLMs sin OOM Crashes",
        date = "2026-08-20",
        readingTime = "9 min de lectura",
        tags = listOf("Android 15-17", "Memory Management", "NDK", "MediaPipe", "Coroutines"),
        isSpanish = true
    ) {
        ArticleSectionHeader("1. El Desafío: Restricciones de Memoria por App en Android 15-17")

        ArticleParagraph(
            "En las versiones modernas del sistema operativo (desde Android 15 hasta Android 17), Google ha incorporado Límites de Memoria por Aplicación mucho más agresivos y daemons de control estricto de memoria (MemoryLimiter:AnonSwap). Cuando un proceso rebasa el umbral asignado, el kernel de Linux fuerza su terminación fulminante sin emitir excepciones capturables."
        )

        ArticleParagraph("Cargar y ejecutar un Modelo de Lenguaje en el dispositivo (incluso empaquetado en cuantización INT4) demanda una cantidad notable de memoria de trabajo:")

        BulletItem("Pesos del modelo:", "~500 MB – 700 MB en disco / RAM")
        BulletItem("Caché KV y buffers de inferencia:", "~150 MB – 300 MB")
        BulletItem("Aplicación base y UI Jetpack Compose:", "~80 MB – 120 MB")

        ArticleParagraph(
            "Sin salvaguardas arquitectónicas calculadas al milímetro, instanciar un modelo local en un teléfono Android de gama media desencadena inevitablemente un cierre por falta de memoria (Out-Of-Memory / OOM)."
        )

        ArticleSectionHeader("2. Estrategia 1: Auditoría de Compatibilidad de Hardware (SueDeviceCompatibility)")

        ArticleParagraph(
            "Un modelo on-device nunca debe descargarse ni inicializarse en hardware que carezca de potencia suficiente. Cumpliendo rigurosamente con Clean Architecture, la comprobación reside en la capa de datos mientras que la entidad de dominio permanece en Kotlin puro sin dependencias del SDK de Android:"
        )

        CodeSnippet(
            code = """// 1. Capa de Dominio: Entidad pura en Kotlin (domain/entities/SueDeviceCompatibility.kt)
data class SueDeviceCompatibility(
    val isSupported: Boolean,
    val reason: SueUnsupportedReason? = null,
    val totalRamBytes: Long = 0L,
    val sdkVersion: Int = 0
)

// 2. Capa de Datos: Comprobación de hardware en SueModelRepositoryImpl
override fun checkDeviceCompatibility(): SueDeviceCompatibility {
    val sdkVersion = Build.VERSION.SDK_INT
    if (sdkVersion < Build.VERSION_CODES.P) {
        return SueDeviceCompatibility(
            isSupported = false,
            reason = SueUnsupportedReason.UNSUPPORTED_ANDROID_VERSION,
            sdkVersion = sdkVersion
        )
    }

    val activityManager = context.getSystemService(Context.ACTIVITY_SERVICE) as? ActivityManager
    val memoryInfo = ActivityManager.MemoryInfo()
    activityManager?.getMemoryInfo(memoryInfo)
    val totalRam = memoryInfo.totalMem
    val isLowRam = activityManager?.isLowRamDevice ?: false
    val minRequiredRam = 2_500_000_000L // Umbral de 2.5 GB

    if (isLowRam || (totalRam in 1 until minRequiredRam)) {
        return SueDeviceCompatibility(
            isSupported = false,
            reason = SueUnsupportedReason.INSUFFICIENT_RAM,
            totalRamBytes = totalRam,
            sdkVersion = sdkVersion
        )
    }
    return SueDeviceCompatibility(
        isSupported = true,
        totalRamBytes = totalRam,
        sdkVersion = sdkVersion
    )
}""",
            language = "Kotlin"
        )

        ArticleParagraph(
            "Si el terminal no cumple los requisitos, la funcionalidad de IA permanece desactivada de forma transparente y la aplicación opera normalmente en modo estándar, preservando la estabilidad del sistema."
        )

        ArticleSectionHeader("3. Estrategia 2: Arranque en Frío Ultraligero (~80 MB)")

        ArticleParagraph(
            "Un error habitual de arquitectura consiste en inicializar los motores de Machine Learning en el método Application.onCreate(). En HomeTutorPro, la aplicación arranca consumiendo únicamente ~80 MB de memoria RAM. Los binarios de MediaPipe y los pesos del modelo se cargan con política estrictamente perezosa (lazy loading) en despachadores de segundo plano en el instante preciso en que el usuario abre la interfaz de voz."
        )

        ArticleSectionHeader("4. Estrategia 3: Calentamiento en Paralelo Durante el Habla")

        ArticleParagraph(
            "Para eliminar cualquier retraso perceptible sin mantener el modelo anclado permanentemente en memoria:"
        )

        BulletItem("1. Despacho Simultáneo:", "Cuando el usuario pulsa el botón del micrófono, SueViewModel activa simultáneamente el SpeechRecognizer en el hilo principal e inicia InferenceRepository.warmUp() en Dispatchers.IO.")
        BulletItem("2. Proyección Concurrente:", "Mientras el profesor formula su pregunta durante 2 a 4 segundos, los pesos del modelo se proyectan en memoria en paralelo.")
        BulletItem("3. Ejecución sin Espera:", "Al terminar la locución, el modelo se encuentra inicializado y preparado para procesar el prompt RAG con 0 ms de retraso percibido por el usuario.")

        ArticleSectionHeader("5. Estrategia 4: Descarga Automática Inteligente por Inactividad")

        ArticleParagraph("Ningún modelo debe permanecer residente en memoria indefinidamente tras su uso. Implementamos una política escalonada de temporizadores con corrutinas:")

        BulletItem("Temporizador de Inactividad (120 segundos):", "Si la interfaz del asistente permanece abierta sin interacción durante 2 minutos (IDLE_RELEASE_TIMEOUT_MS = 120_000L), los recursos se liberan invocando inferenceRepository.release().")
        BulletItem("Temporizador de Cierre (30 segundos):", "Cuando el usuario cierra la vista del asistente, una cuenta atrás de 30 segundos vacía la memoria.")
        BulletItem("Temporizador de Seguridad de Micrófono (15 segundos):", "Protege contra bloqueos del subsistema de audio si se pierde el foco de grabación.")

        CodeSnippet(
            code = """// Temporizador mediante corrutinas en SueViewModel para descarga en reposo
private fun startIdleReleaseTimer(timeoutMs: Long = IDLE_RELEASE_TIMEOUT_MS) {
    idleReleaseJob?.cancel()
    idleReleaseJob = viewModelScope.launch {
        delay(timeoutMs)
        inferenceRepository.release()
    }
}""",
            language = "Kotlin"
        )

        ArticleSectionHeader("6. Estrategia 5: Alineación a Páginas de 16 KB y Estabilidad NDK")

        ArticleParagraph(
            "En terminales con Android 15+ configurados con páginas de memoria de 16 KB, las librerías compartidas (.so) deben compilarse con alineación estricta de segmentos (-Wl,-z,max-page-size=16384)."
        )

        BulletItem("Binarios Pre-Alineados:", "HomeTutorPro integra MediaPipe Tasks GenAI 0.10.27+, cuyas librerías NDK cuentan con pre-alineación para 16 KB.")
        BulletItem("Cero Fallos SIGBUS:", "Las llamadas mmap sobre el archivo cuantizado .task se proyectan limpiamente sobre bloques de 16 KB sin provocar fallos de señal SIGBUS en el kernel.")

        ArticleSectionHeader("7. Estrategia 6: Ciclo de Vida del SO y Liberación en onTrimMemory")

        ArticleParagraph("Vinculamos directamente el ciclo de vida del repositorio de inferencia a los callbacks nativos del sistema operativo Android:")

        CodeSnippet(
            code = """override fun onTrimMemory(level: Int) {
    super.onTrimMemory(level)
    when (level) {
        ComponentCallbacks2.TRIM_MEMORY_UI_HIDDEN,
        ComponentCallbacks2.TRIM_MEMORY_RUNNING_CRITICAL -> {
            // Liberación inmediata de memoria nativa
            inferenceRepository.release()
        }
    }
}""",
            language = "Kotlin"
        )

        ArticleSectionHeader("8. Resumen")

        CalloutBox {
            ArticleParagraph(
                "💡 Conclusión de Arquitectura: Combinando auditorías de compatibilidad de hardware, calentamiento en paralelo durante la captura de voz, descarga automática por inactividad y gestión proactiva de señales del sistema operativo, HomeTutorPro ejecuta modelos generativos en el dispositivo de forma fluida y estable en cientos de modelos de teléfonos sin experimentar un solo cierre por OOM."
            )
        }
    }
}
